export const buildTemplate = (store, assets = {}) => `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Medical Report - Antimicrobial Analysis</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      font-size: 12px;
      line-height: 1.4;
      color: #333;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
    }
    
    .header {
      text-align: center;
      margin-bottom: 30px;
      border-bottom: 2px solid #007bff;
      padding-bottom: 10px;
    }
    
    .header h1 {
      color: #007bff;
      font-size: 24px;
      margin: 0;
    }
    
    .section {
      margin-bottom: 25px;
      page-break-inside: avoid;
    }
    
    .section-title {
      background-color: #f8f9fa;
      border-left: 4px solid #007bff;
      padding: 8px 12px;
      font-size: 16px;
      font-weight: bold;
      color: #007bff;
      margin-bottom: 15px;
    }
    
    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      margin-bottom: 10px;
    }
    
    .info-item {
      padding: 8px 12px;
      background-color: #f8f9fa;
      border-radius: 4px;
      border: 1px solid #e9ecef;
    }
    
    .info-label {
      font-weight: bold;
      color: #495057;
    }
    
    .info-value {
      color: #6c757d;
      margin-left: 5px;
    }
    
    .table-container {
      overflow-x: auto;
      margin: 15px 0;
    }
    
    .mic-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 10px;
    }
    
    .mic-table th,
    .mic-table td {
      border: 1px solid #dee2e6;
      padding: 4px 6px;
      text-align: center;
    }
    
    .mic-table th {
      background-color: #007bff;
      color: white;
      font-weight: bold;
    }
    
    .mic-table td {
      background-color: #f8f9fa;
    }
    
    .interpretation {
      background-color: #e7f3ff;
      border: 1px solid #007bff;
      border-radius: 6px;
      padding: 15px;
      margin: 15px 0;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    
    .interpretation h3 {
      color: #007bff;
      margin-top: 0;
    }
    
    .alert {
      padding: 10px 15px;
      border-radius: 4px;
      margin: 10px 0;
    }
    
    .alert-success {
      background-color: #d4edda;
      border: 1px solid #c3e6cb;
      color: #155724;
    }
    
    .alert-warning {
      background-color: #fff3cd;
      border: 1px solid #ffeaa7;
      color: #856404;
    }
    
    .alert-danger {
      background-color: #f8d7da;
      border: 1px solid #f5c6cb;
      color: #721c24;
    }
    
    .alert-info {
      background-color: #d1ecf1;
      border: 1px solid #bee5eb;
      color: #0c5460;
    }
    
    .chart-container {
      margin: 20px 0;
      text-align: center;
      page-break-inside: avoid;
    }
    
    .chart-container h4 {
      color: #495057;
      margin-bottom: 10px;
      font-size: 14px;
    }
    
    .chart-image {
      max-width: 75%;
      height: auto;
      border: 1px solid #dee2e6;
      border-radius: 4px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    
    .footer {
      margin-top: 30px;
      padding-top: 15px;
      border-top: 1px solid #dee2e6;
      font-size: 10px;
      color: #6c757d;
      text-align: center;
    }
    
    .interpretation-content {
      text-align: left;
      font-size: 11px;
      line-height: 1.5;
    }
    
    .status-success { color: #198754; }
    .status-warning { color: #ffc107; }
    .status-error { color: #dc3545; }
    .status-neutral { color: #6c757d; }
    
    @media print {
      body {
        margin: 0;
        padding: 15px;
      }
      .section {
        page-break-inside: avoid;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <div style="display:flex; align-items:center; justify-content:center; gap:12px;">
      ${assets.logoUrl ? `<img src="${assets.logoUrl}" alt="Logo" style="height:28px; width:auto; display:inline-block;" />` : ''}
      <h1 style="margin:0;">Medical Report - Antimicrobial Analysis</h1>
    </div>
    <p>Generated on ${new Date().toLocaleString()}</p>
  </div>

  <div class="section">
    <div class="section-title">Patient Information</div>
    <div class="info-grid">
      <div class="info-item">
        <span class="info-label">Gender:</span>
        <span class="info-value">${store.patientGender || 'Not specified'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Age:</span>
        <span class="info-value">${store.patientAge || 'Not specified'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Height:</span>
        <span class="info-value">${store.patientHeight || 'Not specified'} cm</span>
      </div>
      <div class="info-item">
        <span class="info-label">Weight:</span>
        <span class="info-value">${store.patientWeight || 'Not specified'} kg</span>
      </div>
      <div class="info-item">
        <span class="info-label">Body Surface Area:</span>
        <span class="info-value">${store.patientSurfaceArea || 'Not calculated'} m²</span>
      </div>
      <div class="info-item">
        <span class="info-label">Creatinine:</span>
        <span class="info-value">${store.patientCreatinine || 'Not specified'} µmol/L</span>
      </div>
      <div class="info-item">
        <span class="info-label">Cockcroft-Gault:</span>
        <span class="info-value">${store.patientCockroft || 'Not calculated'} mL/min</span>
      </div>
      <div class="info-item">
        <span class="info-label">MDRD:</span>
        <span class="info-value">${store.patientMDRD || 'Not calculated'} mL/min/1.73m²</span>
      </div>
      <div class="info-item">
        <span class="info-label">MDRD (adjusted):</span>
        <span class="info-value">${store.patientMDRDCorr || 'Not calculated'} mL/min</span>
      </div>
      <div class="info-item">
        <span class="info-label">CKD-EPI:</span>
        <span class="info-value">${store.patientCKDEPI || 'Not calculated'} mL/min/1.73m²</span>
      </div>
      <div class="info-item">
        <span class="info-label">Continuous Dialysis:</span>
        <span class="info-value">${store.patientDialysisContinue || 'Not specified'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Intermittent Dialysis:</span>
        <span class="info-value">${store.patientDialysisIntermittent || 'Not specified'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Infection Location:</span>
        <span class="info-value">${store.patientInfectionLocation || 'Not specified'}</span>
      </div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Administration Information</div>
    <div class="info-grid">
      <div class="info-item">
        <span class="info-label">Daily Dose:</span>
        <span class="info-value">${store.adminDailyDose || 'Not specified'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Administrations per Day:</span>
        <span class="info-value">${store.nbAdminJour || 'Not specified'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Administration Mode:</span>
        <span class="info-value">${store.modeAdmin || 'Not specified'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Infusion Duration:</span>
        <span class="info-value">${store.durationIV || 'Not specified'} hours</span>
      </div>
      <div class="info-item">
        <span class="info-label">Treatment Start Date:</span>
        <span class="info-value">${store.adminStartDate || 'Not specified'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Last Administration:</span>
        <span class="info-value">${store.adminLastAdmin || 'Not specified'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Sample Collection:</span>
        <span class="info-value">${store.adminSampleCollection || 'Not specified'}</span>
      </div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Antimicrobial Information</div>
    <div class="info-grid">
      <div class="info-item">
        <span class="info-label">Antibiotic:</span>
        <span class="info-value">${store.antibioticText || 'Not selected'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Species:</span>
        <span class="info-value">${store.speciesText || 'Not selected'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Measured Total Concentration:</span>
        <span class="info-value">${store.measuredCon || 'Not specified'} mg/L</span>
      </div>
      ${(store.currentAtbDataRow && store.currentAtbDataRow['Cpeak'] == '1') ? `
      <div class="info-item">
        <span class="info-label">Measured Peak Concentration:</span>
        <span class="info-value">${store.peakConcentration || 'Not specified'} mg/L</span>
      </div>
      ` : ''}
      ${store.currentAtbDataRow?.["pk-pd"] === "AUC" ? `
      <div class="info-item">
        <span class="info-label">Estimated AUC:</span>
        <span class="info-value">${store.estimatedAUC || 'Not specified'} mg.h/L</span>
      </div>
      ` : ''}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Analysis Results</div>
    <div class="info-grid">
      <div class="info-item">
        <span class="info-label">Free Fraction (Fu):</span>
        <span class="info-value">${store.predictedFup || 'Not available'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Expected Free Concentration:</span>
        <span class="info-value">${(() => {
          if (!store.predictedFup || !store.measuredCon) return 'Cannot compute without Fu';
          return parseFloat((store.measuredCon * store.predictedFup).toFixed(2)) + ' mg/L';
        })()}</span>
      </div>
      <div class="info-item">
        <span class="info-label">ECOFF:</span>
        <span class="info-value">${store.ecoff || 'Not available'}</span>
      </div>
      <div class="info-item">
        <span class="info-label">Administration Type:</span>
        <span class="info-value">${store.durationIV === 24 && store.modeAdmin === "IV" ? "IVSE" : "IV"}</span>
      </div>
      ${(() => {
        if (!store.currentAtbDataRow || store.currentAtbDataRow['Ctrough'] != '1') return '';
        const typeIV = store.durationIV === 24 && store.modeAdmin === "IV" ? "IVSE" : "IV";
        const parse = v => { const n = parseFloat(v); return isNaN(n) ? undefined : n; };
        let min, max;
        if (typeIV === "IVSE") {
          min = parse(store.currentAtbDataRow["Cmin usuelle (Plateau_IVSE))"]);
          max = parse(store.currentAtbDataRow["Cmax usuelle (Plateau_IVSE))"]);
        } else {
          min = parse(store.currentAtbDataRow["Cmin usuelle (Trough)"]);
          max = parse(store.currentAtbDataRow["Cmax usuelle (Trough)"]);
        }
        if (min == null || max == null) return '';
        return `
        <div class="info-item">
          <span class="info-label">Therapeutic Range (Total):</span>
          <span class="info-value">[${min} ; ${max}] mg/L</span>
        </div>
        `;
      })()}
      ${(() => {
        if (!store.currentAtbDataRow || store.currentAtbDataRow['Ctrough'] != '1') return '';
        const typeIV = store.durationIV === 24 && store.modeAdmin === "IV" ? "IVSE" : "IV";
        const toxValue = store.currentAtbDataRow[`Conc toxicité ${typeIV}`];
        const toxType = store.currentAtbDataRow[`type toxicité ${typeIV}`];
        if (!toxValue) return '';
        return `
        <div class="info-item">
          <span class="info-label">Toxicity Threshold (Total):</span>
          <span class="info-value">${toxValue} mg/L</span>
        </div>
        ${toxType ? `
        <div class="info-item">
          <span class="info-label">Toxicity Type:</span>
          <span class="info-value">${toxType}</span>
        </div>
        ` : ''}
        `;
      })()}
    </div>
  </div>

  ${store.currentAtbDataRow?.["pk-pd"] === "AUC" ? `
  <div class="section">
    <div class="section-title">AUC Analysis</div>
    <div class="info-grid">
      <div class="info-item">
        <span class="info-label">Target AUC/CMI Ratio:</span>
        <span class="info-value">${store.currentAtbDataRow["AUC/CMI"]}</span>
      </div>
      ${store.estimatedAUC && store.ecoff ? `
      <div class="info-item">
        <span class="info-label">Current AUC/CMI Ratio:</span>
        <span class="info-value">${(store.estimatedAUC / store.ecoff).toFixed(1)}</span>
      </div>
      ` : ''}
      ${(() => {
        const parse = v => { const n = parseFloat(v); return isNaN(n) ? undefined : n; };
        const min = parse(store.currentAtbDataRow["AUCmin usuelle"]);
        const max = parse(store.currentAtbDataRow["AUCmax usuelle"]);
        if (!min || !max) return '';
        return `
        <div class="info-item">
          <span class="info-label">AUC Therapeutic Range:</span>
          <span class="info-value">[${min} ; ${max}] mg.h/L</span>
        </div>
        `;
      })()}
      ${store.currentAtbDataRow["AUC toxicité"] ? `
      <div class="info-item">
        <span class="info-label">AUC Toxicity Threshold:</span>
        <span class="info-value">${store.currentAtbDataRow["AUC toxicité"]} mg.h/L</span>
      </div>
      ${store.currentAtbDataRow["type AUC toxicité"] ? `
      <div class="info-item">
        <span class="info-label">AUC Toxicity Type:</span>
        <span class="info-value">${store.currentAtbDataRow["type AUC toxicité"]}</span>
      </div>
      ` : ''}
      ` : ''}
    </div>
  </div>
  ` : ''}

  ${store.rowValues && store.rowValues.length > 0 ? `
  <div class="section">
    <div class="section-title">MIC Distribution</div>
    <div class="table-container">
      <table class="mic-table">
        <thead>
          <tr>
            <th>0.002</th><th>0.004</th><th>0.008</th><th>0.016</th><th>0.03</th>
            <th>0.06</th><th>0.125</th><th>0.25</th><th>0.5</th><th>1</th>
            <th>2</th><th>4</th><th>8</th><th>16</th><th>32</th>
            <th>64</th><th>128</th><th>256</th><th>512</th>
            <th>Dist</th><th>Obs</th><th>ECOFF</th><th>CI</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            ${store.rowValues.map(value => `<td>${value || '0'}</td>`).join('')}
          </tr>
        </tbody>
      </table>
    </div>
    
    ${store.imageUrl ? `
    <div class="chart-container">
      <h4>MIC Distribution Chart</h4>
      <img src="${store.imageUrl}" alt="MIC Distribution Chart" class="chart-image" />
    </div>
    ` : ''}
  </div>
  ` : ''}

  <div class="section">
    <div class="section-title">Interpretation Summary</div>
    
    <!-- ECOFF Interpretation (only for fT PK/PD antibiotics) -->
    ${store.currentAtbDataRow?.["pk-pd"] === "fT" ? `
    <div class="interpretation">
      <h3>ECOFF Comparison</h3>
      ${(() => {
        if (!store.predictedFup || !store.measuredCon) {
          return '<div class="alert alert-warning">Cannot compute free concentration without Fu value.</div>';
        }
        const expectedConcentration = parseFloat((store.measuredCon * store.predictedFup).toFixed(2));
        if (store.ecoff === 0 || store.ecoff === "ID") {
          return '<div class="alert alert-info">No ECOFF data available.</div>';
        }
        const isAbove = expectedConcentration >= store.ecoff * 4;
        return `<div class="alert ${isAbove ? 'alert-success' : 'alert-warning'}">
          <strong>Theoretical free concentration</strong> (${expectedConcentration}mg/L) is <strong>${isAbove ? 'higher' : 'lower'}</strong> than <strong>ECOFF</strong> × 4 (${store.ecoff * 4}mg/L) associated with the organism/antibiotic pair.
        </div>`;
      })()}
    </div>
    ` : ''}
    
    <!-- Therapeutic Range Interpretation (Total) -->
    ${(() => {
      if (!store.currentAtbDataRow || store.currentAtbDataRow['Ctrough'] != '1' || !store.measuredCon) return '';
      const typeIV = store.durationIV === 24 && store.modeAdmin === "IV" ? "IVSE" : "IV";
      const parse = v => { const n = parseFloat(v); return isNaN(n) ? undefined : n; };
      let min, max;
      if (typeIV === "IVSE") {
        min = parse(store.currentAtbDataRow["Cmin usuelle (Plateau_IVSE))"]);
        max = parse(store.currentAtbDataRow["Cmax usuelle (Plateau_IVSE))"]);
      } else {
        min = parse(store.currentAtbDataRow["Cmin usuelle (Trough)"]);
        max = parse(store.currentAtbDataRow["Cmax usuelle (Trough)"]);
      }
      if (min == null || max == null) return '';
      
      let status, message;
      if (store.measuredCon < min) {
        status = 'alert-warning';
        message = `<strong>Total concentration</strong> (${store.measuredCon}mg/L) is <strong>below</strong> the therapeutic dosage range (${min} - ${max}mg/L).`;
      } else if (store.measuredCon > max) {
        status = 'alert-warning';
        message = `<strong>Total concentration</strong> (${store.measuredCon}mg/L) is <strong>over</strong> the therapeutic dosage range (${min} - ${max}mg/L).`;
      } else {
        status = 'alert-success';
        message = `<strong>Total concentration</strong> (${store.measuredCon}mg/L) is <strong>within</strong> the therapeutic dosage range (${min} - ${max}mg/L).`;
      }
      
      return `
      <div class="interpretation">
        <h3>Total Concentration Interpretation</h3>
        <div class="alert ${status}">
          ${message}
        </div>
      </div>
      `;
    })()}
    
    <!-- Toxicity Assessment (Total) -->
    <div class="interpretation">
      <h3>Toxicity Assessment</h3>
      ${(() => {
        if (!store.currentAtbDataRow || store.currentAtbDataRow['Ctrough'] != '1' || !store.measuredCon) {
          return '<div class="alert alert-info">Toxicity data not available.</div>';
        }
        const typeIV = store.durationIV === 24 && store.modeAdmin === "IV" ? "IVSE" : "IV";
        const toxValue = store.currentAtbDataRow[`Conc toxicité ${typeIV}`];
        const toxType = store.currentAtbDataRow[`type toxicité ${typeIV}`];
        
        if (!toxValue) {
          return `<div class="alert alert-info"><strong>Toxicity</strong> data not available for <strong>${typeIV}</strong> type of administration.</div>`;
        }
        
        const isAbove = store.measuredCon > toxValue;
        const toxicityInfo = toxType ? `, potential <strong>${toxType}</strong> toxicity` : '';
        
        return `<div class="alert ${isAbove ? 'alert-danger' : 'alert-success'}">
          <strong>Total concentration</strong> (${store.measuredCon}mg/L) is <strong>${isAbove ? 'higher than' : 'below'}</strong> the <strong>toxicity threshold</strong> (${toxValue}mg/L)${isAbove ? toxicityInfo : ''} for <strong>${typeIV}</strong> type of administration.
        </div>`;
      })()}
    </div>

    <!-- Peak Concentration Interpretation (Cpeak) -->
    ${(() => {
      if (!store.currentAtbDataRow || store.currentAtbDataRow['Cpeak'] != '1' || !store.peakConcentration) return '';
      const parse = v => { const n = parseFloat(v); return isNaN(n) ? undefined : n; };
      const min = parse(store.currentAtbDataRow['Cmin usuel (peak)']);
      const max = parse(store.currentAtbDataRow['Cmax usuel (peak)']);
      if (min == null || max == null) return '';
      
      let status, message;
      if (store.peakConcentration < min) {
        status = 'alert-warning';
        message = `<strong>Peak concentration</strong> (${store.peakConcentration}mg/L) is <strong>below</strong> the therapeutic range (${min} - ${max}mg/L).`;
      } else if (store.peakConcentration > max) {
        status = 'alert-warning';
        message = `<strong>Peak concentration</strong> (${store.peakConcentration}mg/L) is <strong>over</strong> the therapeutic range (${min} - ${max}mg/L).`;
      } else {
        status = 'alert-success';
        message = `<strong>Peak concentration</strong> (${store.peakConcentration}mg/L) is <strong>within</strong> the therapeutic range (${min} - ${max}mg/L).`;
      }
      
      const cpeakToxValue = store.currentAtbDataRow['Cpeak toxicité'];
      const cpeakToxType = store.currentAtbDataRow['type Cpeak toxicité'];
      const toxBlock = cpeakToxValue != null ? `
        <div class="alert ${store.peakConcentration > cpeakToxValue ? 'alert-danger' : 'alert-success'}">
          <strong>Peak concentration</strong> (${store.peakConcentration}mg/L) is <strong>${store.peakConcentration > cpeakToxValue ? 'higher than' : 'below'}</strong> the <strong>toxicity threshold</strong> (${cpeakToxValue}mg/L)${store.peakConcentration > cpeakToxValue && cpeakToxType ? `, potential <strong>${cpeakToxType}</strong> toxicity` : ''}.
        </div>
      ` : '<div class="alert alert-info">Peak toxicity data not available.</div>';
      
      return `
      <div class="interpretation">
        <h3>Peak Concentration Interpretation</h3>
        <div class="alert ${status}">${message}</div>
        ${toxBlock}
      </div>
      `;
    })()}
  </div>

  ${store.errorMIC ? `
  <div class="section">
    <div class="alert alert-danger">
      <strong>Error:</strong> ${store.errorMIC}
    </div>
  </div>
  ` : ''}

  <div class="footer">
    <p>This report was automatically generated on the website https://www.antibiomic.com</p>
    <p>Report ID: ${Date.now()} | Generated: ${new Date().toLocaleString()}</p>
    <p><strong>Contact:</strong> contact@antibiomic.com</p>
  </div>
</body>
</html>`; 